import React from 'react';
import { Box, Typography, Paper, Grid, useTheme, alpha, IconButton, Tooltip, Avatar, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoIcon from '@mui/icons-material/Info';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
    },
}));

const WelcomeSection = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    borderRadius: theme.spacing(2),
    backdropFilter: 'blur(8px)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const StatCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
    },
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(8px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
        borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        opacity: 0.8,
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 48,
    height: 48,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    color: theme.palette.primary.main,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        transform: 'scale(1.05) rotate(5deg)',
    },
}));

const ChartCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    height: '100%',
    borderRadius: theme.spacing(2),
    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(8px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.05)}`,
}));

const ActivityItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1.5),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateX(4px)',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    },
    '&:last-child': {
        marginBottom: 0,
    },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: 24,
    borderRadius: 12,
    '& .MuiChip-label': {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: theme.spacing(0, 1.5),
    },
}));

type StatusColor = 'success' | 'warning' | 'info' | 'error' | 'primary';

interface ActivityItem {
    title: string;
    time: string;
    status: StatusColor;
}

// Sample data for the chart
const chartData = [
    { name: 'Jan', students: 400, requests: 240, active: 320 },
    { name: 'Feb', students: 300, requests: 139, active: 280 },
    { name: 'Mar', students: 200, requests: 980, active: 240 },
    { name: 'Apr', students: 278, requests: 390, active: 300 },
    { name: 'May', students: 189, requests: 480, active: 260 },
    { name: 'Jun', students: 239, requests: 380, active: 290 },
];

export const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <DashboardContainer>
            <WelcomeSection elevation={0}>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mb: 3,
                    flexDirection: { xs: 'column', sm: 'row' },
                    textAlign: { xs: 'center', sm: 'left' }
                }}>
                    <Avatar
                        sx={{
                            width: { xs: 56, sm: 64 },
                            height: { xs: 56, sm: 64 },
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        }}
                    >
                        A
                    </Avatar>
                    <Box>
                        <Typography 
                            variant="h4" 
                            gutterBottom 
                            fontWeight="bold" 
                            color="primary"
                            sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                        >
                            {t('admin.dashboard.welcome')}
                        </Typography>
                        <Typography 
                            variant="subtitle1" 
                            color="textSecondary"
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            {t('admin.dashboard.welcomeSubtitle')}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                }}>
                    <StatusChip
                        label={t('common.status.active')}
                        color="success"
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
                    />
                    <StatusChip
                        label={t('admin.dashboard.role.administrator')}
                        color="primary"
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                </Box>
            </WelcomeSection>

            <Grid container spacing={{ xs: 2, sm: 3 }}>
                {/* Statistics Cards */}
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <StatCard>
                        <IconWrapper>
                            <SchoolIcon />
                        </IconWrapper>
                        <Typography 
                            variant="h6" 
                            color="textSecondary" 
                            gutterBottom
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            {t('admin.dashboard.totalSchools')}
                        </Typography>
                        <Typography 
                            variant="h3" 
                            component="div" 
                            fontWeight="bold" 
                            color="primary"
                            sx={{ fontSize: { xs: '1.75rem', sm: '2.5rem' } }}
                        >
                            25
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ color: 'success.main', mr: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                            <Typography 
                                variant="body2" 
                                color="success.main"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                                {t('admin.dashboard.stats.increaseThisMonth', { count: 3 })}
                            </Typography>
                        </Box>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <StatCard>
                        <IconWrapper>
                            <PeopleIcon />
                        </IconWrapper>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {t('admin.dashboard.totalUsers')}
                        </Typography>
                        <Typography variant="h3" component="div" fontWeight="bold" color="primary">
                            4,567
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="body2" color="success.main">
                                {t('admin.dashboard.stats.increaseFromLastMonth', { percent: 12 })}
                            </Typography>
                        </Box>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <StatCard>
                        <IconWrapper>
                            <PendingActionsIcon />
                        </IconWrapper>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {t('admin.dashboard.pendingApprovals')}
                        </Typography>
                        <Typography variant="h3" component="div" fontWeight="bold" color="primary">
                            15
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ color: 'warning.main', mr: 1 }} />
                            <Typography variant="body2" color="warning.main">
                                {t('admin.dashboard.stats.newToday', { count: 5 })}
                            </Typography>
                        </Box>
                    </StatCard>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <StatCard>
                        <IconWrapper>
                            <SchoolIcon />
                        </IconWrapper>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {t('admin.dashboard.systemHealth')}
                        </Typography>
                        <Typography variant="h3" component="div" fontWeight="bold" color="primary">
                            99%
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                            <TrendingUpIcon sx={{ color: 'success.main', mr: 1 }} />
                            <Typography variant="body2" color="success.main">
                                {t('admin.dashboard.stats.increaseFromLastMonth', { percent: 1 })}
                            </Typography>
                        </Box>
                    </StatCard>
                </Grid>

                {/* Chart Section */}
                <Grid item xs={12} lg={8}>
                    <ChartCard>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold">
                                {t('admin.dashboard.activityOverview')}
                            </Typography>
                            <Tooltip title={t('admin.dashboard.chartInfo')}>
                                <IconButton size="small">
                                    <InfoIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ height: 300 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={chartData}>
                                    <defs>
                                        <linearGradient id="colorStudents" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={theme.palette.secondary.main} stopOpacity={0.2}/>
                                            <stop offset="95%" stopColor={theme.palette.secondary.main} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.1)} />
                                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                                    <YAxis stroke={theme.palette.text.secondary} />
                                    <RechartsTooltip 
                                        contentStyle={{
                                            background: theme.palette.background.paper,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: theme.spacing(1),
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
                                        }}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="students"
                                        stroke={theme.palette.primary.main}
                                        fillOpacity={1}
                                        fill="url(#colorStudents)"
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="requests"
                                        stroke={theme.palette.secondary.main}
                                        fillOpacity={1}
                                        fill="url(#colorRequests)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Box>
                    </ChartCard>
                </Grid>

                {/* Recent Activity Section */}
                <Grid item xs={12} lg={4}>
                    <ChartCard>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                            {t('admin.dashboard.recentActivity')}
                        </Typography>
                        <Box sx={{ mt: 2 }}>
                            {[
                                { 
                                    title: t('admin.dashboard.activity.newSchoolAdded'), 
                                    time: t('admin.dashboard.activity.timeAgo', { time: 1, unit: 'hour' }), 
                                    status: 'success' as StatusColor 
                                },
                                { 
                                    title: t('admin.dashboard.activity.userPermissionUpdate'), 
                                    time: t('admin.dashboard.activity.timeAgo', { time: 3, unit: 'hours' }), 
                                    status: 'warning' as StatusColor 
                                },
                                { 
                                    title: t('admin.dashboard.activity.systemMaintenance'), 
                                    time: t('admin.dashboard.activity.timeAgo', { time: 1, unit: 'day' }), 
                                    status: 'info' as StatusColor 
                                },
                            ].map((item: ActivityItem, index) => (
                                <ActivityItem key={index}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            {item.title}
                                        </Typography>
                                        <StatusChip
                                            label={item.status}
                                            color={item.status}
                                            size="small"
                                            sx={{ bgcolor: alpha(theme.palette[item.status].main, 0.1) }}
                                        />
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        {item.time}
                                    </Typography>
                                </ActivityItem>
                            ))}
                        </Box>
                    </ChartCard>
                </Grid>
            </Grid>
        </DashboardContainer>
    );
}; 